import axios from "axios";
import { resolveGatewayBackedServiceBaseUrl } from "./serviceBaseUrl";
import { isKnownAccountDeletionSsoProvider } from "../constants/accountDeletionSsoProviders";

const API_BASE_URL = resolveGatewayBackedServiceBaseUrl(process.env.REACT_APP_API_URL);
const ACCOUNT_URL = `${API_BASE_URL}/api/auth/account`;

const JSON_HEADERS = Object.freeze({ "Content-Type": "application/json" });
const CREDENTIAL_REQUEST_CONFIG = Object.freeze({
    withCredentials: true,
    skipAuthRefresh: true,
    skipRequestLogging: true,
});
const SESSIONLESS_REQUEST_CONFIG = Object.freeze({
    credentials: "omit",
    cache: "no-store",
});

export class AccountDeletionApiError extends Error {
    constructor({ status = null, code = null, retryAfter = null } = {}) {
        super("Account deletion request failed");
        this.name = "AccountDeletionApiError";
        this.status = status;
        this.code = code;
        this.retryAfter = retryAfter;
    }
}

const safeError = (error) => new AccountDeletionApiError({
    status: Number.isInteger(error?.response?.status) ? error.response.status : null,
    code: typeof error?.response?.data?.code === "string" ? error.response.data.code : null,
    retryAfter: typeof error?.response?.headers?.["retry-after"] === "string"
        ? error.response.headers["retry-after"]
        : null,
});

const safeFetchError = async (response) => {
    let code = null;
    try {
        const contentType = response.headers?.get?.("content-type") ?? "";
        if (contentType.includes("application/json")) {
            const data = await response.json();
            if (typeof data?.code === "string") {
                code = data.code;
            }
        }
    } catch {
        code = null;
    }
    return new AccountDeletionApiError({
        status: Number.isInteger(response?.status) ? response.status : null,
        code,
        retryAfter: response.headers?.get?.("retry-after") ?? null,
    });
};

const responseDataOrNull = async (response) => {
    if (response.status === 204) {
        return null;
    }
    const text = await response.text();
    if (!text) {
        return null;
    }
    try {
        return JSON.parse(text);
    } catch {
        return null;
    }
};

const requestHeaders = (body, headers) => (
    body !== undefined || headers
        ? { ...JSON_HEADERS, ...headers }
        : undefined
);

const request = async (method, url, { body, headers } = {}) => {
    try {
        const resolvedHeaders = requestHeaders(body, headers);
        const response = await axios({
            ...CREDENTIAL_REQUEST_CONFIG,
            method,
            url,
            ...(body === undefined ? {} : { data: body }),
            ...(resolvedHeaders ? { headers: resolvedHeaders } : {}),
        });
        return response?.data ?? null;
    } catch (error) {
        throw safeError(error);
    }
};

const sessionlessRequest = async (method, url, { body, headers } = {}) => {
    try {
        const resolvedHeaders = requestHeaders(body, headers);
        const response = await fetch(url, {
            ...SESSIONLESS_REQUEST_CONFIG,
            method,
            ...(body === undefined ? {} : { body: JSON.stringify(body) }),
            ...(resolvedHeaders ? { headers: resolvedHeaders } : {}),
        });
        if (!response.ok) {
            throw await safeFetchError(response);
        }
        return responseDataOrNull(response);
    } catch (error) {
        if (error instanceof AccountDeletionApiError) {
            throw error;
        }
        throw new AccountDeletionApiError();
    }
};

const requireString = (value, fieldName) => {
    if (typeof value !== "string" || value.trim() === "") {
        throw new TypeError(`${fieldName} is required`);
    }
    return value.trim();
};

const requirePassword = (value) => {
    if (typeof value !== "string" || value.length === 0) {
        throw new TypeError("password is required");
    }
    return value;
};

const requireSsoProvider = (value) => {
    const provider = requireString(value, "provider").toLowerCase();
    if (!isKnownAccountDeletionSsoProvider(provider)) {
        throw new TypeError("provider is unsupported");
    }
    return provider;
};

const intendedDeletionPayload = ({ confirmation, reasonCode }) => {
    if (confirmation !== "DELETE") {
        throw new TypeError("confirmation must be DELETE");
    }
    const payload = { confirmation };
    if (reasonCode !== undefined && reasonCode !== null && reasonCode !== "") {
        payload.reasonCode = requireString(reasonCode, "reasonCode");
    }
    return payload;
};

export const createAccountDeletionIdempotencyKey = () => {
    if (typeof window === "undefined" || typeof window.crypto?.randomUUID !== "function") {
        throw new Error("Secure UUID generation is unavailable");
    }
    return window.crypto.randomUUID().toLowerCase();
};

export const accountDeletionService = {
    getSsoCapabilities: async () => sessionlessRequest(
        "GET",
        `${ACCOUNT_URL}/deletion-sso-capabilities`,
    ),

    getCsrfToken: async () => request("GET", `${ACCOUNT_URL}/csrf-token`),

    getDeletionPreview: async () => request("GET", `${ACCOUNT_URL}/deletion-preview`),

    startPasswordAuthorization: async ({ password, confirmation, reasonCode, csrfToken }) => request(
        "POST",
        `${ACCOUNT_URL}/deletion-authorizations/password/start`,
        {
            body: {
                ...intendedDeletionPayload({ confirmation, reasonCode }),
                password: requirePassword(password),
            },
            headers: {
                "X-CSRF-Token": requireString(csrfToken, "csrfToken"),
            },
        },
    ),

    completePasswordAuthorization: async ({
        challengeId,
        code,
        confirmation,
        reasonCode,
        csrfToken,
    }) => request(
        "POST",
        `${ACCOUNT_URL}/deletion-authorizations/password/complete`,
        {
            body: {
                ...intendedDeletionPayload({ confirmation, reasonCode }),
                challengeId: requireString(challengeId, "challengeId"),
                code: requireString(code, "code"),
            },
            headers: {
                "X-CSRF-Token": requireString(csrfToken, "csrfToken"),
            },
        },
    ),

    startSsoAuthorization: async ({ provider, confirmation, reasonCode, csrfToken }) => {
        const requiredProvider = requireSsoProvider(provider);
        return request(
            "POST",
            `${ACCOUNT_URL}/deletion-authorizations/sso/${requiredProvider}/start`,
            {
                body: intendedDeletionPayload({ confirmation, reasonCode }),
                headers: {
                    "X-CSRF-Token": requireString(csrfToken, "csrfToken"),
                },
            },
        );
    },

    exchangeSsoAuthorization: async ({
        provider,
        state,
        code,
        confirmation,
        reasonCode,
        csrfToken,
    }) => {
        const requiredProvider = requireSsoProvider(provider);
        return request(
            "POST",
            `${ACCOUNT_URL}/deletion-authorizations/sso/${requiredProvider}/exchange`,
            {
                body: {
                    ...intendedDeletionPayload({ confirmation, reasonCode }),
                    state: requireString(state, "state"),
                    code: requireString(code, "code"),
                },
                headers: {
                    "X-CSRF-Token": requireString(csrfToken, "csrfToken"),
                },
            },
        );
    },

    startRestrictedAuthorization: async ({
        email,
        password,
        confirmation,
        reasonCode,
    }) => sessionlessRequest(
        "POST",
        `${ACCOUNT_URL}/deletion-authorizations/restricted/start`,
        {
            body: {
                email: requireString(email, "email"),
                password: requirePassword(password),
                ...intendedDeletionPayload({ confirmation, reasonCode }),
            },
        },
    ),

    completeRestrictedAuthorization: async ({
        challengeId,
        code,
        confirmation,
        reasonCode,
    }) => {
        const requiredChallengeId = requireString(challengeId, "challengeId");
        return sessionlessRequest(
            "POST",
            `${ACCOUNT_URL}/deletion-authorizations/restricted/challenges/`
                + `${encodeURIComponent(requiredChallengeId)}/complete`,
            {
                body: {
                    ...intendedDeletionPayload({ confirmation, reasonCode }),
                    code: requireString(code, "code"),
                },
            },
        );
    },

    startRestrictedSsoAuthorization: async ({ email, provider, confirmation, reasonCode }) => {
        const requiredProvider = requireSsoProvider(provider);
        return sessionlessRequest(
            "POST",
            `${ACCOUNT_URL}/deletion-authorizations/restricted/sso/${requiredProvider}/start`,
            {
                body: {
                    email: requireString(email, "email"),
                    ...intendedDeletionPayload({ confirmation, reasonCode }),
                },
            },
        );
    },

    exchangeRestrictedSsoAuthorization: async ({
        provider,
        state,
        code,
        confirmation,
        reasonCode,
    }) => {
        const requiredProvider = requireSsoProvider(provider);
        return sessionlessRequest(
            "POST",
            `${ACCOUNT_URL}/deletion-authorizations/restricted/sso/${requiredProvider}/exchange`,
            {
                body: {
                    ...intendedDeletionPayload({ confirmation, reasonCode }),
                    state: requireString(state, "state"),
                    code: requireString(code, "code"),
                },
            },
        );
    },

    createDeletionRequest: async ({
        confirmation,
        reasonCode,
        csrfToken,
        reauthToken,
        idempotencyKey,
    }) => {
        const requestFn = csrfToken === undefined || csrfToken === null
            ? sessionlessRequest
            : request;
        return requestFn("POST", `${ACCOUNT_URL}/deletion-requests`, {
            body: intendedDeletionPayload({ confirmation, reasonCode }),
            headers: {
                "Idempotency-Key": requireString(idempotencyKey, "idempotencyKey"),
                ...(csrfToken === undefined || csrfToken === null
                    ? {}
                    : { "X-CSRF-Token": requireString(csrfToken, "csrfToken") }),
                "X-Reauth-Token": requireString(reauthToken, "reauthToken"),
            },
        });
    },

    startRecovery: async ({ email, password }) => sessionlessRequest(
        "POST",
        `${ACCOUNT_URL}/deletion-recovery/start`,
        {
            body: {
                email: requireString(email, "email"),
                password: requirePassword(password),
            },
        },
    ),

    completeRecoveryChallenge: async ({ challengeId, code }) => {
        const requiredChallengeId = requireString(challengeId, "challengeId");
        return sessionlessRequest(
            "POST",
            `${ACCOUNT_URL}/deletion-recovery/challenges/${encodeURIComponent(requiredChallengeId)}/complete`,
            {
                body: {
                    code: requireString(code, "code"),
                },
            },
        );
    },

    startRecoverySso: async ({ email, provider }) => {
        const requiredProvider = requireSsoProvider(provider);
        return sessionlessRequest(
            "POST",
            `${ACCOUNT_URL}/deletion-recovery/sso/${requiredProvider}/start`,
            {
                body: { email: requireString(email, "email") },
            },
        );
    },

    exchangeRecoverySso: async ({ provider, state, code }) => {
        const requiredProvider = requireSsoProvider(provider);
        return sessionlessRequest(
            "POST",
            `${ACCOUNT_URL}/deletion-recovery/sso/${requiredProvider}/exchange`,
            {
                body: {
                    state: requireString(state, "state"),
                    code: requireString(code, "code"),
                },
            },
        );
    },

    getRecoveryStatus: async ({ recoveryToken }) => sessionlessRequest(
        "GET",
        `${ACCOUNT_URL}/deletion-recovery/status`,
        {
            headers: {
                "X-Recovery-Token": requireString(recoveryToken, "recoveryToken"),
            },
        },
    ),

    confirmRecovery: async ({
        recoveryToken,
        confirmationToken,
        idempotencyKey,
    }) => sessionlessRequest(
        "POST",
        `${ACCOUNT_URL}/deletion-recovery/confirm`,
        {
            body: { confirmation: "RESTORE" },
            headers: {
                "Idempotency-Key": requireString(idempotencyKey, "idempotencyKey"),
                "X-Recovery-Token": requireString(recoveryToken, "recoveryToken"),
                "X-Recovery-Confirmation": requireString(
                    confirmationToken,
                    "confirmationToken",
                ),
            },
        },
    ),
};
